import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import {
  AuthMethod,
  CompanyType,
  deleteV1CompaniesCompanyIdAccountsId,
  MatchKeyMatchableType,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { type AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { complianceStatusColumn } from '~components/CompliancePanel/complianceStatusColumn';
import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DeactivateConfirmationDialog from '~components/DeactivateConfirmationDialog/DeactivateConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { DriverForm, DriverFormValues } from '~components/DriverForm/DriverForm';
import { InvitesToSendToggleGroup } from '~components/FormFields/v2/ToggleButtonInput';
import LabelList from '~components/Labels/LabelList';
import { matchKeysColumn } from '~components/MatchKeys/matchKeysColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { StatusTabFilters } from '~components/StatusTabFilters/StatusTabFilters';
import {
  UserForm,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/v2/UserForm';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { AccountTypesFilter } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import useEntityCsvConfig from '~hooks/useCSVImportExport/useEntityCsvConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { useDriverForm } from '~hooks/useDriverForm/useDriverForm';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useEquipmentForm } from '~hooks/useEquipmentForm/useEquipmentForm';
import { useUserForm } from '~hooks/useUserForm/useUserForm';
import { User } from '~hooks/useUsers/models';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import useUsers from '~hooks/useUsers/useUsers';
import { Filters } from '~pages/Settings/Accounts/useAccountFilters';
import {
  EquipmentForm,
  EquipmentFormValues,
} from '~pages/Settings/Administration/EquipmentManagement/EquipmentForm';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { InvitesToSendState } from '~pages/Settings/UserManagement/UserManagementDataGrid';
import { CAPACITY_BREAKDOWN_QUERY_KEY } from '~queries/useCapacityBreakdownQuery';
import useCompanyMatchKeysQuery from '~queries/useCompanyMatchKeysQuery';
import { ROUTES } from '~router/routePaths';
import connection from '~services/connectionModule';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AccountFilters from './AccountFilters';
import { AccountSaveChange, AccountsDialog } from './AccountsDialog';
import CapacityBreakdown from './CapacityBreakdown/CapacityBreakdown';
import ConnectAccountDialog from './ConnectAccountDialog';
import VendorGuideShareBanner from './VendorGuideShare/VendorGuideShareBanner';

enum VendorsTab {
  ACCOUNTS = 'accounts',
  CAPACITY_BREAKDOWN = 'capacity_breakdown',
}

const ActionsWrapper = styled(Box)(({ theme }) => ({
  gap: theme.spacing(1.5),
}));

const ConnectAccountDialogCTA = ({ onConnected }: { onConnected: () => void }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  return (
    <>
      <SmallButton
        data-test-id={'connect-account-cta-btn'}
        color="brandV2Yellow"
        onClick={openDialog}
        startIcon={<Add />}
      >
        {t('actions.connect')}
      </SmallButton>

      <ConnectAccountDialog
        isDialogOpen={isDialogOpen}
        onClose={closeDialog}
        onConnected={onConnected}
      />
    </>
  );
};

const AccountsDataGrid = observer(() => {
  const theme = useTheme();

  const hideDocumentTypesEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDocumentTypes,
  });
  const typedTruckCapacityEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.typedTruckCapacityV1,
  });
  const location = useLocation();
  const isCustomerPage = location.pathname.includes('customers');
  const isVendorPage = location.pathname.includes('vendors');
  const canShowManageButton = !isCustomerPage;
  const canShowCapacityBreakdownTab = isVendorPage && typedTruckCapacityEnabled;
  const settingsIndex = ROUTES.settings.base;

  const queriedAccountType = isCustomerPage
    ? AccountTypesFilter.CUSTOMER
    : AccountTypesFilter.VENDOR;

  const getAccountType = useCallback(() => {
    return [
      queriedAccountType as AccountTypesFilter.CUSTOMER | AccountTypesFilter.VENDOR,
    ];
  }, [queriedAccountType]);

  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState<boolean>(false);
  const [selectedAccountId, setSelectedAccountId] = useState<Nullable<string>>(null);
  const [editedAccount, setEditedAccount] = useState<Account>();
  const [selectedAccountCompanyId, setSelectedAccountCompanyId] =
    useState<Nullable<string>>(null);
  const [selectedAccountAddress, setSelectedAccountAddress] =
    useState<Nullable<AddressItem>>(null);
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);

  const { getAllCompanyAccounts } = useAccount();
  const { setSearchQueryValue, searchValue } = useDataGridSearch();
  const { toasterStore, userStore, companyAssetsStore } = useStores();
  const queryClient = useQueryClient();
  const companyId = getEffectiveUserCompanyId(userStore);
  const matchKeysEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.matchKeysEnabled,
  });
  const showMatchKeys = matchKeysEnabled && userStore.getPermissions().canViewMatchKey;
  const { matchKeysByMatchableId } = useCompanyMatchKeysQuery({
    companyId,
    isEnabled: showMatchKeys,
    matchableType: MatchKeyMatchableType.ACCOUNT,
  });
  const {
    driverDialogRef,
    driverFormIsDirty,
    isDriverDialogOpen,
    setIsDriverDialogOpen,
    driverInviteType,
    driverInviteTypeError,
    setDriverInviteTypeError,
    onDriverFormStateChange,
    onDriverInviteTypeChange,
    createNewDriver,
  } = useDriverForm();
  const { createDriverByCompanyId, isLoading: isLoadingDrivers } = useDrivers();
  const {
    userDialogRef,
    onUserFormStateChange,
    isUserDialogOpen,
    setIsUserDialogOpen,
    userInvitesToSend,
    onChangeUserInvite,
    userFormIsDirty,
    createNewUser,
  } = useUserForm();
  const { isUpdating: isUpdatingUsers, createUser } = useUsers();
  const { getUserRoles } = useUserRoles();
  const {
    equipmentFormRef,
    isEquipmentFormOpen,
    setIsEquipmentFormOpen,
    isEquipmentFormDirty,
    createNewEquipment,
    onEquipmentFormStateChange,
  } = useEquipmentForm();
  const { createEquipmentByCompanyId } = useEquipment();
  const [isUpdatingEquipment, setIsUpdatingEquipment] = useState(false);

  const [accountIdToDelete, setAccountIdToDelete] = useState<string>();
  const [isDeactivatingAccount, setIsDeactivatingAccount] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const isMutating =
    isDeactivatingAccount ||
    isSavingAccount ||
    isUpdatingUsers ||
    isUpdatingEquipment ||
    isLoadingDrivers;

  const { config: csvConfig, loadConfig: loadCsvConfig } = useEntityCsvConfig(
    isCustomerPage ? 'customers' : 'vendors',
    companyId,
  );

  const accountFilters = companyAssetsStore.accountFilters;
  const requestFilters = useMemo<Filters>(() => {
    const hasManualCompanyTypeFilter = Boolean(
      accountFilters.connected_company?.company_types?.length,
    );

    if (isCustomerPage || hasManualCompanyTypeFilter) {
      return accountFilters;
    }

    return {
      ...accountFilters,
      connected_company: {
        company_types: [
          CompanyType.BROKER,
          CompanyType.CONTRACTOR,
          CompanyType.HAULER,
          CompanyType.PRODUCER,
        ],
      },
    };
  }, [accountFilters, isCustomerPage]);

  const {
    goToPage: goToAccountsPage,
    isFetching: isFetchingAccounts,
    pagination: accountsPagination,
    reloadCurrentPage: reloadAccountsPage,
    rows,
    startOver: startAccountsOver,
  } = useCursorPagination<Account>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      getAllCompanyAccounts({
        accountTypes: getAccountType(),
        companyId,
        cursor,
        filters: requestFilters,
        limit,
        link,
        searchQuery: searchValue,
        signal,
      }),
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) =>
      reportRequestError(
        error,
        t('error_messages.account.failed_to_fetch_company_accounts'),
      ),
  });
  const isAccountsBusy = isFetchingAccounts || isMutating;

  const csvModal = useCSVModal({ onImportComplete: () => void goToAccountsPage() });

  useEffect(() => {
    getUserRoles();
  }, [getUserRoles]);

  const userPermissions = useMemo(() => {
    return userStore.getPermissions();
  }, [userStore.getPermissions()]);

  const selectedAccount = useMemo(() => {
    return rows.find((account) => account.id === selectedAccountId) ?? editedAccount;
  }, [selectedAccountId, rows, editedAccount]);
  const createNew = () => {
    setSelectedAccountId(null);
    setIsAccountDialogOpen(true);
  };
  const addUser = (account: Account) => {
    setAsyncErrors([]);
    setSelectedAccountId(account.id);
    setSelectedAccountCompanyId(account.connectedCompany?.id);
    createNewUser();
  };
  const addEquipment = (account: Account) => {
    setAsyncErrors([]);
    setSelectedAccountId(account.id);
    setSelectedAccountCompanyId(account.connectedCompany?.id);
    createNewEquipment();
  };
  const addDriver = (account: Account) => {
    setAsyncErrors([]);
    setSelectedAccountId(account.id);
    setSelectedAccountCompanyId(account.connectedCompany?.id);
    setSelectedAccountAddress(account.address);
    createNewDriver();
  };

  const editRow = (id: string) => {
    setSelectedAccountId(id);
    setEditedAccount(rows.find((account) => account.id === id));
    setIsAccountDialogOpen(true);
  };

  const handleDeleteAccount = useCallback(async () => {
    if (!accountIdToDelete) {
      return Promise.resolve();
    }

    try {
      setIsDeactivatingAccount(true);
      await deleteV1CompaniesCompanyIdAccountsId({
        path: { 'company-id': companyId, id: accountIdToDelete },
      });

      toasterStore.push(
        alert(t('administration.vendors.account_deactivated'), AlertTypes.success),
      );
      void reloadAccountsPage();
      setAccountIdToDelete(undefined);

      return Promise.resolve();
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_deactivate'),
      );
    } finally {
      setIsDeactivatingAccount(false);
    }
  }, [accountIdToDelete, companyId, reloadAccountsPage, toasterStore]);

  const handleAccountSaved = (change: AccountSaveChange) => {
    if (change === 'created') {
      void goToAccountsPage();
    } else {
      void reloadAccountsPage();
    }
  };

  const handleCloseAccountDialog = () => {
    setIsAccountDialogOpen(false);
    setSelectedAccountId(null);
    setEditedAccount(undefined);
    void queryClient.invalidateQueries({ queryKey: [CAPACITY_BREAKDOWN_QUERY_KEY] });
  };
  const handleCloseUserDialog = () => {
    setIsUserDialogOpen(false);
    setSelectedAccountId(null);
    setSelectedAccountCompanyId(null);
  };
  const handleCloseDriverDialog = () => {
    setIsDriverDialogOpen(false);
    setSelectedAccountId(null);
    setSelectedAccountCompanyId(null);
    setSelectedAccountAddress(null);
  };
  const handleCloseEquipmentDialog = () => {
    setIsEquipmentFormOpen(false);
    setIsEquipmentFormOpen(false);
    setSelectedAccountId(null);
  };
  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  const onSubmitUserForm = async () => {
    const userData = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
      (resolve) => {
        userDialogRef.current?.submit((data) => resolve(data));
      },
    );

    if (userData) {
      await onUserSubmitCallback(userData, userInvitesToSend);
    }
  };

  const onUserSubmitCallback = async (
    data: UserValidationDTOWithSitesDiff,
    userNotificationsPreferenceData?: InvitesToSendState,
  ) => {
    const shouldSendEmailInvite = Boolean(
      !userNotificationsPreferenceData?.email.isDisabled &&
      userNotificationsPreferenceData?.email.shouldSend,
    );
    const shouldSendSmsInvite = Boolean(
      !userNotificationsPreferenceData?.sms.isDisabled &&
      userNotificationsPreferenceData?.sms.shouldSend,
    );
    const invitesToSend = { email: shouldSendEmailInvite, sms: shouldSendSmsInvite };

    try {
      await createUser(
        data as unknown as User,
        invitesToSend,
        companyId,
        selectedAccountCompanyId as string,
      );
      setIsUserDialogOpen(false);
      const name = `${data?.firstName} ${data?.lastName}`;
      toasterStore.push(alert(t('user.user_created', { name }), AlertTypes.success));
    } catch (error) {
      const e = error as AxiosError<ModelError_Response>;

      setAsyncErrors(e.response?.data.error.errors || []);
    }
  };

  const onSubmitEquipmentForm = async () => {
    const equipmentData = await new Promise<EquipmentFormValues | undefined>(
      (resolve) => {
        equipmentFormRef.current?.submit((data) => resolve(data));
      },
    );

    if (equipmentData) {
      await onEquipmentSubmitCallback(equipmentData);
    }
  };

  const onEquipmentSubmitCallback = async (data: EquipmentFormValues) => {
    setIsUpdatingEquipment(true);
    try {
      const equipment = await createEquipmentByCompanyId({
        equipment: data as EquipmentFormValues,
        companyId,
        managedCompanyId: selectedAccountCompanyId as string,
      });
      setIsEquipmentFormOpen(false);
      toasterStore.push(
        alert(
          t('administration.equipment.equipment_created', {
            name: equipment?.name,
            equipment_id: equipment?.equipmentId,
          }),
          AlertTypes.success,
        ),
      );
    } catch (error) {
      const e = error as AxiosError<ModelError_Response>;

      setAsyncErrors(e.response?.data.error.errors || []);
    } finally {
      setIsUpdatingEquipment(false);
    }
  };

  const onSubmitDriverForm = async () => {
    const isInviteTypeMissing = driverInviteType.length === 0;
    setDriverInviteTypeError(isInviteTypeMissing ? t('invite_type.required') : undefined);

    const driverData = await new Promise<DriverFormValues | undefined>((resolve) => {
      driverDialogRef.current?.submit((data) => resolve(data));
    });

    if (isInviteTypeMissing) {
      return;
    }

    if (driverData) {
      await onDriverSubmitCallback(driverData);
    }
  };

  const onDriverSubmitCallback = async (data: DriverFormValues) => {
    const shouldSendEmailInvite = driverInviteType.includes('email');
    const shouldSendSmsInvite = driverInviteType.includes('sms');
    const invitesToSend = { email: shouldSendEmailInvite, sms: shouldSendSmsInvite };

    try {
      await createDriverByCompanyId(
        data,
        invitesToSend,
        selectedAccountCompanyId,
        selectedAccountAddress,
      );
      setIsDriverDialogOpen(false);
      const name = `${data?.firstName} ${data?.lastName}`;
      toasterStore.push(alert(t('user.user_created', { name }), AlertTypes.success));
      void reloadAccountsPage();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const e = error as AxiosError<ModelError_Response>;

        setAsyncErrors(e.response?.data.error.errors || []);
      } else {
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_create_driver'),
        );
      }
    }
  };

  // fetchPage reads these through useCursorPagination's latest-callback ref, so they are listed to restart from page 1 when any changes
  useEffect(() => {
    if (companyId) {
      void startAccountsOver();
    }
  }, [
    companyId,
    searchValue,
    requestFilters,
    queriedAccountType,
    userStore.isCurrentCompanyActive,
    startAccountsOver,
  ]);

  // Reset the filters when the account page changes
  useEffect(() => {
    companyAssetsStore.setAccountFilters({});
  }, [queriedAccountType]);

  const [vendorsTab, setVendorsTab] = useState<VendorsTab>(VendorsTab.ACCOUNTS);
  const isCapacityBreakdownTabSelected =
    canShowCapacityBreakdownTab && vendorsTab === VendorsTab.CAPACITY_BREAKDOWN;

  const vendorsTabFilters = [
    { value: VendorsTab.ACCOUNTS, label: t('administration.vendors.accounts_tab') },
    {
      value: VendorsTab.CAPACITY_BREAKDOWN,
      label: t('administration.vendors.capacity_breakdown_tab'),
    },
  ];

  const accountsColumns = useMemo<Array<DataTableColumn<Account>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.account_name'),
        valueGetter: (account) => account.name,
      },
      {
        field: 'connectedCompanyTreadId',
        headerName: t('form_fields.tread_id'),
        valueGetter: (account) => account.connectedCompany?.treadId || '',
      },
      {
        field: 'department',
        headerName: t('form_fields.department'),
        valueGetter: (account) => account.department?.name || '',
      },
      {
        field: 'externalId',
        headerName: t('form_fields.external_id'),
        valueGetter: (account) => account.externalId || '',
      },
      {
        field: 'accountingId',
        headerName: t('form_fields.accounting_id'),
        valueGetter: (account) => account.accountingId || '',
      },
      ...(isCustomerPage
        ? []
        : [
            {
              field: 'companyType',
              headerName: t('form_fields.company_type'),
              valueGetter: (account: Account) =>
                startCase(account.connectedCompany?.companyType) || '',
            },
            {
              field: 'truckCount',
              headerName: t('account.form.committed_truck_capacity'),
              valueGetter: (account: Account) =>
                account.connectedAccount?.truckCount || '',
            },
            ...(!hideDocumentTypesEnabled ? [complianceStatusColumn<Account>()] : []),
          ]),
      ...(showMatchKeys ? [matchKeysColumn<Account>(matchKeysByMatchableId)] : []),
      {
        field: 'labels',
        headerName: t('form_fields.labels'),
        renderCell: (account) => <LabelList labels={account.labels ?? []} />,
      },
      {
        field: 'address.fullAddress',
        headerName: t('form_fields.address'),
        valueGetter: (account) => {
          if (!account.address) {
            return '';
          }

          const { streetAddress, premise, locality, country, administrativeArea } =
            account.address;

          return `${streetAddress}${premise ? ` ${premise},` : ','} ${locality}, ${administrativeArea}, ${country?.name ?? ''}`;
        },
      },
      // TODO: use a shared created-on column — https://linear.app/treadapp/issue/TRE-18155
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (account) => (
          <Tooltip
            title={`${t('common.last_update')}: ${account.updatedAt ? dateFormat(account.updatedAt) : ''}`}
          >
            <Typography component="span">
              {account.createdAt ? dateFormat(account.createdAt) : ''}
            </Typography>
          </Tooltip>
        ),
      },
      {
        defaultHidden: true,
        field: 'notes',
        headerName: t('form_fields.notes'),
        valueGetter: (account) => account.notes,
      },
      actionsColumn((account) => {
        const options = [];
        const showManagedCompanyActions = account.managed;

        if (showManagedCompanyActions) {
          options.push({
            title: t('actions.add_user'),
            callBack: () => addUser(account),
          });

          options.push({
            title: t('actions.add_equipment'),
            callBack: () => addEquipment(account),
          });

          options.push({
            title: t('actions.add_driver'),
            callBack: () => addDriver(account),
          });
        }

        if (userPermissions.canDeleteAccount) {
          options.push({
            title: t('actions.deactivate'),
            icon: <ToggleOffOutlined />,
            callBack: () => setAccountIdToDelete(account.id),
          });
        }

        return (
          <Box sx={{ alignItems: 'center', display: 'flex' }}>
            {canShowManageButton && account.connectedCompany?.id && account.managed && (
              <Link
                to={`/${settingsIndex}/vendors/${account.connectedCompany.id}/manage`}
              >
                <SmallButton
                  color="brandV2Black"
                  variant="outlined"
                  sx={{ mr: 0.5, color: 'text.primary', borderColor: 'divider' }}
                >
                  {t('actions.manage')}
                </SmallButton>
              </Link>
            )}
            {userPermissions.canEditAccount && (
              <IconButton
                aria-label={t('actions.edit')}
                data-ui="edit-account"
                size="small"
                onClick={() => editRow(account.id)}
              >
                <Edit />
              </IconButton>
            )}
            <SimpleMenu options={options} size="small">
              <MoreHoriz />
            </SimpleMenu>
          </Box>
        );
      }),
    ],
    [
      userPermissions,
      canShowManageButton,
      hideDocumentTypesEnabled,
      isCustomerPage,
      matchKeysByMatchableId,
      showMatchKeys,
    ],
  );

  const hasHeaderActions =
    userPermissions.canCreateAccount || userPermissions.canConnectCompany;

  return (
    <>
      <SettingsPageHeader
        title={
          isCustomerPage
            ? t('page_headings.customers')
            : isVendorPage
              ? t('page_headings.vendors')
              : t('page_headings.account_management')
        }
        description={
          isCustomerPage
            ? t('settings.page_descriptions.customers')
            : isVendorPage
              ? t('settings.page_descriptions.vendors')
              : t('settings.page_descriptions.account_management')
        }
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mr: -3,
        }}
      >
        {canShowCapacityBreakdownTab && (
          <Box>
            <StatusTabFilters
              value={vendorsTab}
              onChange={setVendorsTab}
              filters={vendorsTabFilters}
            />
          </Box>
        )}

        {isVendorPage && <VendorGuideShareBanner />}

        {isCapacityBreakdownTabSelected ? (
          <CapacityBreakdown />
        ) : (
          <>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                mr: 3,
              }}
            >
              <AccountFilters />
              {hasHeaderActions && (
                <ActionsWrapper sx={{ display: 'flex' }}>
                  {userPermissions.canCreateAccount && (
                    <CSVImportExportButton onClick={csvModal.open} />
                  )}

                  {userPermissions.canConnectCompany && (
                    <ConnectAccountDialogCTA
                      onConnected={() => void goToAccountsPage()}
                    />
                  )}

                  {userPermissions.canCreateAccount && (
                    <SmallButton
                      data-test-id={'create-company-btn'}
                      color="brandV2Yellow"
                      onClick={createNew}
                      startIcon={<Add />}
                    >
                      {t('account.create_account')}
                    </SmallButton>
                  )}
                </ActionsWrapper>
              )}
            </Box>
            <DataTable
              id="admin-accounts-grid"
              sx={{ maxWidth: `calc(100% - ${theme.spacing(3)})` }}
              columns={accountsColumns}
              rows={rows}
              loading={isAccountsBusy}
              onSearchChange={onChangeQuickFilter}
              headerTrailingComponent={
                <DataGridPager
                  isFetching={isAccountsBusy}
                  onNavigate={(link) => void goToAccountsPage(link)}
                  pagination={accountsPagination}
                />
              }
            />
          </>
        )}
      </Box>
      <AccountsDialog
        defaultAccount={selectedAccount}
        onClose={handleCloseAccountDialog}
        onSavingChange={setIsSavingAccount}
        onSuccess={handleAccountSaved}
        open={isAccountDialogOpen}
        source={isCustomerPage ? 'customers-page' : 'vendors-page'}
      />
      <DeactivateConfirmationDialog
        id={accountIdToDelete}
        message={t('administration.vendors.deactivate_account_dialog_message')}
        onClose={() => setAccountIdToDelete(undefined)}
        onConfirm={handleDeleteAccount}
        title={t('administration.vendors.deactivate_account_dialog_title')}
      />
      <Dialog isOpen={isUserDialogOpen} onClose={handleCloseUserDialog}>
        <DialogHeader onClose={handleCloseUserDialog}>
          {t('user.create_user')}
        </DialogHeader>

        <DialogContent>
          <UserForm
            ref={userDialogRef}
            allowSharing
            availableAuthMethods={[AuthMethod.OTP_SMS, AuthMethod.PASSWORD]}
            defaultUser={null}
            editable
            errors={asyncErrors}
            isManagedUser
            managedCompanyId={selectedAccountCompanyId as string}
            onFormStateChange={onUserFormStateChange}
            scopedCompanyId={companyId}
          />
        </DialogContent>

        <DialogActions>
          {userInvitesToSend && (
            <Box sx={{ mr: 'auto' }}>
              <InvitesToSendToggleGroup
                invitesToSend={userInvitesToSend}
                isUpdating={isUpdatingUsers}
                onChangeInvite={onChangeUserInvite}
              />
            </Box>
          )}

          <SmallButton
            color="secondary"
            disabled={isUpdatingUsers}
            onClick={handleCloseUserDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={!userFormIsDirty || isUpdatingUsers}
            loading={isUpdatingUsers}
            onClick={onSubmitUserForm}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog isOpen={isEquipmentFormOpen} onClose={handleCloseEquipmentDialog}>
        <DialogHeader onClose={handleCloseEquipmentDialog}>
          {t('administration.equipment.create_internal_truck')}
        </DialogHeader>

        <DialogContent>
          <EquipmentForm
            ref={equipmentFormRef}
            companyShares={[]}
            defaultEquipment={null}
            errors={asyncErrors}
            isManagedEquipment
            managedCompanyId={selectedAccountCompanyId as string}
            onFormStateChange={onEquipmentFormStateChange}
            selectedCompanyId={null}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isUpdatingEquipment}
            onClick={handleCloseEquipmentDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={isUpdatingEquipment || !isEquipmentFormDirty}
            loading={isUpdatingEquipment}
            onClick={onSubmitEquipmentForm}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog isOpen={isDriverDialogOpen} onClose={handleCloseDriverDialog}>
        <DialogHeader onClose={handleCloseDriverDialog}>
          {t(`user.create_driver`)}
        </DialogHeader>

        <DialogContent>
          <DriverForm
            ref={driverDialogRef}
            companyId={companyId}
            errors={asyncErrors}
            inviteTypes={driverInviteType}
            inviteTypeError={driverInviteTypeError}
            isManagedDriver
            isUpdating={isLoadingDrivers}
            managedCompanyId={selectedAccountCompanyId ?? undefined}
            onInviteTypesChange={onDriverInviteTypeChange}
            onFormStateChange={onDriverFormStateChange}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isLoadingDrivers}
            onClick={handleCloseDriverDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={!driverFormIsDirty || isLoadingDrivers}
            loading={isLoadingDrivers}
            onClick={onSubmitDriverForm}
          >
            {t(`actions.create`)}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={csvConfig}
        loadConfig={loadCsvConfig}
        companyId={companyId}
        entityDisplayName={isCustomerPage ? t('common.customers') : t('common.vendors')}
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});
export { AccountsDataGrid };
